/**
 * @packageDocumentation
 * @module f1/fn
 */

export interface Curry2 {
  /**
   * Creates a partially applied function of two (2) parameters
   *
   * @public
   * @function f1.c2
   * @param fn the function to curry
   * @example
   *
   * ```ts
   * const addInc = (a: number, b: number) => b + a + 1;
   * const addInc2 = f1.c2(addInc);
   *
   * addInc2(1, 2) => 4
   * addInc2(1)(2) => 4
   *
   * const addTwoAndInc = addInc2(2);
   * addTwoAndInc(4) => 7 // 4 + 2 + 1
   * ```
   */
  <A, B, V>(fn: (a: A, b: B) => V): (a: A, b?: B) => V | ((b: B) => V);
}

/**
 * @see Curry2
 */
export default <A, B, V>(fn: (a: A, b: B) => V) => {
  function f(a: A, b: B): V;
  function f(a: A): (b: B) => V;
  function f(a: A, b?: B): V | ((b: B) => V) {
    return arguments.length < 2 ? (b: B) => fn(a, b) : fn(a, b as B);
  }

  return f;
};
